import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import './CreerCandidatureModal.css';

// ── Types ──────────────────────────────────────────────────────────────────

interface LotTechnique {
  id: number;
  nom: string;
  code: number;
}

interface Region {
  id: number;
  nom: string;
}

interface LotDepartementRef {
  id: number;
}

interface Departement {
  id: number;
  nom: string;
  id_region: number;
  region?: Region;
  lots_departements?: LotDepartementRef[];
}

interface RegionGroup {
  regionId: number;
  regionNom: string;
  departements: Departement[];
}

interface EntiteResult {
  id: number;
  nom: string;
  type: string;
  entreprise_id: number | null;
}

type TypeCandidature = 'solo' | 'groupement';

interface CreerCandidatureModalProps {
  accordCadreId: number;
  onClose: () => void;
  onSuccess: (candidatureId: number) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function isRegional(code: number): boolean {
  return code >= 100 && code < 200;
}

function isDepartemental(code: number): boolean {
  return code >= 200 && code < 300;
}

/**
 * Extrait le premier lot_departement.id d'un departement.
 * Retourne null si aucun.
 */
function getLotDeptId(dept: Departement): number | null {
  return dept.lots_departements?.[0]?.id ?? null;
}

// ── Composant ──────────────────────────────────────────────────────────────

const CreerCandidatureModal: React.FC<CreerCandidatureModalProps> = ({
  accordCadreId,
  onClose,
  onSuccess,
}) => {
  const apiUrl = process.env.REACT_APP_API_URL ?? '';
  const token  = localStorage.getItem('token') ?? '';
  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  // ── État ──────────────────────────────────────────────────────
  const [lotsTechniques, setLotsTechniques]     = useState<LotTechnique[]>([]);
  const [departements, setDepartements]         = useState<Departement[]>([]);
  const [loadingData, setLoadingData]           = useState(true);
  const [lotTechniqueId, setLotTechniqueId]     = useState<number | ''>('');
  const [selectedLotIds, setSelectedLotIds]     = useState<Set<number>>(new Set());
  const [submitting, setSubmitting]             = useState(false);
  const [error, setError]                       = useState<string | null>(null);
  // ── État étape 2 ────────────────────────────────────────
  const [step, setStep]                         = useState<1 | 2>(1);
  const [typeCandidature, setTypeCandidature]   = useState<TypeCandidature>('solo');
  const [membres, setMembres]                   = useState<EntiteResult[]>([]);
  const [searchQuery, setSearchQuery]           = useState('');
  const [searchResults, setSearchResults]       = useState<EntiteResult[]>([]);
  const [isSearching, setIsSearching]           = useState(false);
  const searchTimeout                           = useRef<ReturnType<typeof setTimeout> | null>(null);
  // ── Chargement initial ────────────────────────────────────────
  useEffect(() => {
    setLoadingData(true);
    Promise.all([
      fetch(`${apiUrl}/lots-technique`, { headers }).then((r) => r.json()),
      fetch(`${apiUrl}/departements`, { headers }).then((r) => r.json()),
    ])
      .then(([lotsTechData, deptsData]) => {
        setLotsTechniques(
          Array.isArray(lotsTechData?.data) ? lotsTechData.data : []
        );
        setDepartements(
          Array.isArray(deptsData?.data) ? deptsData.data : []
        );
      })
      .catch(() => setError('Impossible de charger les données. Veuillez réessayer.'))
      .finally(() => setLoadingData(false));
  }, [apiUrl, headers]);

  // Réinitialise la sélection quand le lot technique change
  useEffect(() => {
    setSelectedLotIds(new Set());
  }, [lotTechniqueId]);

  // ── Lot technique sélectionné ──────────────────────────────────
  const selectedLot = useMemo(
    () => lotsTechniques.find((l) => l.id === lotTechniqueId) ?? null,
    [lotsTechniques, lotTechniqueId]
  );

  // ── Groupes régions (lot 1xx) ──────────────────────────────────
  const regionGroups = useMemo((): RegionGroup[] => {
    if (!selectedLot || !isRegional(selectedLot.code)) return [];

    const map = new Map<number, RegionGroup>();
    for (const dept of departements) {
      if (!dept.region) continue;
      const rid = dept.region.id;
      if (!map.has(rid)) {
        map.set(rid, {
          regionId:   rid,
          regionNom:  dept.region.nom,
          departements: [],
        });
      }
      map.get(rid)!.departements.push(dept);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.regionNom.localeCompare(b.regionNom, 'fr')
    );
  }, [selectedLot, departements]);

  // ── Départements pour lot 2xx ──────────────────────────────────
  const departementsList = useMemo((): Departement[] => {
    if (!selectedLot || !isDepartemental(selectedLot.code)) return [];
    return [...departements].sort((a, b) =>
      a.nom.localeCompare(b.nom, 'fr')
    );
  }, [selectedLot, departements]);

  // ── Handlers ──────────────────────────────────────────────────

  /** Toggle un lot_departement individuel */
  const toggleLot = useCallback((lotId: number) => {
    setSelectedLotIds((prev) => {
      const next = new Set(prev);
      if (next.has(lotId)) next.delete(lotId);
      else next.add(lotId);
      return next;
    });
  }, []);

  /**
   * Coche/décoche tous les lots d'une région.
   * Cocher = cocher tous les depts de la région.
   * Décocher = retirer tous les depts de la région.
   */
  const toggleRegion = useCallback((group: RegionGroup) => {
    const regionLotIds = group.departements
      .map((d) => getLotDeptId(d))
      .filter((id): id is number => id !== null);

    const allSelected = regionLotIds.every((id) => selectedLotIds.has(id));

    setSelectedLotIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        regionLotIds.forEach((id) => next.delete(id));
      } else {
        regionLotIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [selectedLotIds]);

  /** État de la case région (checked, indeterminate) */
  const getRegionCheckboxState = useCallback(
    (group: RegionGroup): { checked: boolean; indeterminate: boolean } => {
      const regionLotIds = group.departements
        .map((d) => getLotDeptId(d))
        .filter((id): id is number => id !== null);
      const selectedCount = regionLotIds.filter((id) => selectedLotIds.has(id)).length;
      if (selectedCount === 0) return { checked: false, indeterminate: false };
      if (selectedCount === regionLotIds.length) return { checked: true, indeterminate: false };
      return { checked: false, indeterminate: true };
    },
    [selectedLotIds]
  );

  // ── Soumission ─────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!lotTechniqueId) return;

    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        accord_cadre_id:    accordCadreId,
        lot_technique_id:   lotTechniqueId,
        lot_departement_ids: Array.from(selectedLotIds),
      };

      const res = await fetch(`${apiUrl}/publications/ac27/candidatures`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        const msg =
          data?.errors
            ? Object.values(data.errors as Record<string, string[]>)
                .flat()
                .join(' ')
            : data?.message ?? 'Une erreur est survenue.';
        setError(msg);
        return;
      }

      const candidatureId: number =
        data?.candidature?.id ?? data?.data?.id ?? data?.id;

      if (!candidatureId) {
        setError('Candidature créée mais identifiant introuvable.');
        return;
      }

      // 2. Si groupement : un seul POST avec les entite_ids
      // CORE se charge du provisioning entreprise si besoin
      if (typeCandidature === 'groupement' && membres.length > 0) {
        const entiteIds = membres.map((m) => m.id);
        await fetch(
          `${apiUrl}/publications/ac27/candidatures/${candidatureId}/inviter-membre`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ entite_ids: entiteIds }),
          }
        );
      }

      onSuccess(candidatureId);
    } catch {
      setError('Erreur réseau. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Recherche autocomplete (débounce 300 ms) ────────────────────
  const handleSearchChange = (q: string) => {
    setSearchQuery(q);
    setSearchResults([]);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (q.trim().length < 2) { setIsSearching(false); return; }
    setIsSearching(true);
    searchTimeout.current = setTimeout(() => {
      fetch(
        `${apiUrl}/entites/autocomplete/${encodeURIComponent(q.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
        .then((r) => r.json())
        .then((d) => {
          const results: EntiteResult[] = Array.isArray(d?.data) ? d.data : [];
          setSearchResults(results.filter((r) => !membres.some((m) => m.id === r.id)));
        })
        .catch(() => setSearchResults([]))
        .finally(() => setIsSearching(false));
    }, 300);
  };

  const handleAddMembre = (entite: EntiteResult) => {
    if (!membres.some((m) => m.id === entite.id)) {
      setMembres((prev) => [...prev, entite]);
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleRemoveMembre = (entiteId: number) => {
    setMembres((prev) => prev.filter((m) => m.id !== entiteId));
  };

  // ── Fermeture sur Escape ───────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // ── Peut avancer à l'étape 2 ────────────────────────────
  const canGoNext = !!lotTechniqueId && selectedLotIds.size > 0;
  // ── Peut soumettre ───────────────────────────────────
  const canSubmit = canGoNext && !submitting;

  // ── Rendu ──────────────────────────────────────────────────────
  return (
    <div
      className="creer-candidature-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="creer-candidature-modal" role="dialog" aria-modal="true">

        {/* ── Header ── */}
        <div className="creer-candidature-modal-header">
          <div className="creer-candidature-header-left">
            <h2>Créer une candidature</h2>
            <div className="creer-candidature-steps">
              <span className={`creer-candidature-step${step === 1 ? ' active' : ' done'}`}>1. Lots</span>
              <span className="creer-candidature-step-sep">›</span>
              <span className={`creer-candidature-step${step === 2 ? ' active' : ''}`}>2. Type</span>
            </div>
          </div>
          <button
            className="creer-candidature-modal-close"
            onClick={onClose}
            aria-label="Fermer"
            type="button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* ── Body ── */}
        <div className="creer-candidature-modal-body">

          {/* Erreur globale */}
          {error && (
            <div className="creer-candidature-error">{error}</div>
          )}

          {step === 1 && (
            <>
          {/* Chargement */}
          {loadingData ? (
            <div className="creer-candidature-loading">
              <span className="creer-candidature-spinner" />
              Chargement des données…
            </div>
          ) : (
            <>
              {/* ── Sélection lot technique ── */}
              <div>
                <label className="creer-candidature-section-label">
                  Lot technique
                </label>
                <select
                  className="creer-candidature-select"
                  value={lotTechniqueId}
                  onChange={(e) =>
                    setLotTechniqueId(
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                >
                  <option value="">— Sélectionnez un lot —</option>
                  {lotsTechniques.map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      Lot {lt.code} — {lt.nom}
                    </option>
                  ))}
                </select>
              </div>

              {/* ── Sélection lots départements ── */}
              {selectedLot && (
                <div>
                  <label className="creer-candidature-section-label">
                    {isRegional(selectedLot.code)
                      ? 'Régions couvertes'
                      : 'Départements couverts'}
                  </label>

                  {/* Lot régional (1xx) */}
                  {isRegional(selectedLot.code) && (
                    <>
                      {regionGroups.length === 0 ? (
                        <p className="creer-candidature-empty">
                          Aucune région disponible.
                        </p>
                      ) : (
                        <div className="creer-candidature-lots-container">
                          {regionGroups.map((group) => {
                            const { checked } = getRegionCheckboxState(group);
                            return (
                              <label
                                key={group.regionId}
                                className="creer-candidature-lot-item"
                                onClick={() => toggleRegion(group)}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleRegion(group)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <span className="creer-candidature-lot-item-label">
                                  {group.regionNom}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}

                  {/* Lot départemental (2xx) */}
                  {isDepartemental(selectedLot.code) && (
                    <>
                      {departementsList.length === 0 ? (
                        <p className="creer-candidature-empty">
                          Aucun département disponible.
                        </p>
                      ) : (
                        <div className="creer-candidature-lots-container">
                          {departementsList.map((dept) => {
                            const lotId = getLotDeptId(dept);
                            if (lotId === null) return null;
                            return (
                              <label
                                key={dept.id}
                                className="creer-candidature-lot-item"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedLotIds.has(lotId)}
                                  onChange={() => toggleLot(lotId)}
                                />
                                <span className="creer-candidature-lot-item-label">
                                  {dept.nom}
                                </span>
                                <span className="creer-candidature-lot-item-code">
                                  #{String(dept.id).padStart(2, '0')}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}

                  {/* Compteur de sélection */}
                  {selectedLotIds.size > 0 && (
                    <p className="creer-candidature-selection-count">
                      <strong>{selectedLotIds.size}</strong>{' '}
                      lot{selectedLotIds.size > 1 ? 's' : ''} sélectionné
                      {selectedLotIds.size > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
            </>
          )}

          {step === 2 && (
            <div className="creer-candidature-step2">
              <label className="creer-candidature-section-label">Type de candidature</label>
              <div className="creer-candidature-type-choices">
                <label className={`creer-candidature-type-option${typeCandidature === 'solo' ? ' selected' : ''}`}>
                  <input
                    type="radio"
                    name="typeCandidature"
                    value="solo"
                    checked={typeCandidature === 'solo'}
                    onChange={() => setTypeCandidature('solo')}
                  />
                  <div>
                    <span className="creer-candidature-type-label">Candidature solo</span>
                    <span className="creer-candidature-type-desc">
                      Vous candidatez seul(e), sans groupement.
                    </span>
                  </div>
                </label>
                <label className={`creer-candidature-type-option${typeCandidature === 'groupement' ? ' selected' : ''}`}>
                  <input
                    type="radio"
                    name="typeCandidature"
                    value="groupement"
                    checked={typeCandidature === 'groupement'}
                    onChange={() => setTypeCandidature('groupement')}
                  />
                  <div>
                    <span className="creer-candidature-type-label">Candidature en groupement</span>
                    <span className="creer-candidature-type-desc">
                      Vous invitez des partenaires à rejoindre votre candidature.
                    </span>
                  </div>
                </label>
              </div>

              {typeCandidature === 'groupement' && (
                <div className="creer-candidature-membres">
                  <label className="creer-candidature-section-label">Inviter des membres</label>

                  <div className="creer-candidature-search-wrapper">
                    <input
                      type="text"
                      className="creer-candidature-search-input"
                      placeholder="Rechercher un prestataire par nom…"
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      autoComplete="off"
                    />
                    {isSearching && <span className="creer-candidature-search-spinner" />}

                    {searchResults.length > 0 && (
                      <div className="creer-candidature-search-results">
                        {searchResults.map((entite) => (
                          <button
                            key={entite.id}
                            type="button"
                            className="creer-candidature-search-result-item"
                            onClick={() => handleAddMembre(entite)}
                          >
                            <span className="creer-candidature-result-nom">{entite.nom}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchQuery.trim().length >= 2 && !isSearching && searchResults.length === 0 && (
                      <div className="creer-candidature-search-empty">Aucun résultat.</div>
                    )}
                  </div>

                  {membres.length > 0 && (
                    <div className="creer-candidature-membres-list">
                      <p className="creer-candidature-membres-count">
                        {membres.length} membre{membres.length > 1 ? 's' : ''} invité{membres.length > 1 ? 's' : ''}
                      </p>
                      {membres.map((m) => (
                        <div key={m.id} className="creer-candidature-membre-row">
                          <span className="creer-candidature-membre-nom">{m.nom}</span>
                          <button
                            type="button"
                            className="creer-candidature-membre-remove"
                            onClick={() => handleRemoveMembre(m.id)}
                            aria-label={`Retirer ${m.nom}`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="creer-candidature-modal-footer">
          {step === 1 ? (
            <>
              <button
                type="button"
                className="creer-candidature-btn-cancel"
                onClick={onClose}
              >
                Annuler
              </button>
              <button
                type="button"
                className="creer-candidature-btn-submit"
                onClick={() => { setError(null); setStep(2); }}
                disabled={!canGoNext || loadingData}
              >
                Suivant ›
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="creer-candidature-btn-cancel"
                onClick={() => { setError(null); setStep(1); }}
                disabled={submitting}
              >
                ‹ Précédent
              </button>
              <button
                type="button"
                className="creer-candidature-btn-submit"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {submitting && (
                  <span className="creer-candidature-btn-submit-spinner" />
                )}
                Créer la candidature
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreerCandidatureModal;
